import * as d3 from 'd3';
import dagre from 'dagre';

import {assert} from 'Asserts';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  ComplexPolymerType,
  ConnectionSideGraphQL,
  OLIGO_DUPLEX_REVERSE_STRAND_REF,
  ResolvedAttachmentGraphQL,
  SequenceEntityGraphQL,
  SimplePolymerGraphQL,
} from 'modbio/complex-polymers/ComplexPolymerConstants';
import {getWidthForBaseCount} from 'modbio/polymer-maps/OligoMap';
import {letterStartX} from 'molbio/monomers/ModificationVisualizationUtil';

import {getBackbonePolymerFromResolvedAttachment, isPolymerBackboneStrand} from './ComplexPolymerUtil';
import {
  DEFAULT_CONJUGATE_NODE_HEIGHT,
  DEFAULT_CONJUGATE_NODE_WIDTH,
  EdgeType,
  HandleType,
  NUCLEOTIDE_NODE_HEIGHT,
  NUCLEOTIDE_NODE_WIDTH,
  NodeType,
  NucleotideStructureGraphNodeData,
  PolymerStructureGraph,
  StructureGraphEdge,
  StructureGraphNode,
} from './PolymerStructureGraphConstants';

export function generateConjugateStructureGraphNode(
  helmReference: string,
  entityId: string
): StructureGraphNode {
  return {
    id: helmReference,
    type: NodeType.CONJUGATE_NODE,
    position: {
      // TODO: Position conjugate structure graph nodes
      x: 0,
      y: 0,
    },
    draggable: true,
    selectable: true,
    data: {
      entityId,
      polymerRef: helmReference,
      width: DEFAULT_CONJUGATE_NODE_WIDTH,
      height: DEFAULT_CONJUGATE_NODE_HEIGHT,
    },
  };
}

// Y Position of reverse strand in an oligo duplex, accounting for the height of the forward strand and any
// margin between the two strands
export const REVERSE_STRAND_NUCLEOTIDE_NODE_Y_POSITION = 40;

export function generateBackbonePolymerStructureGraphNodes(
  complexPolymerType: ComplexPolymerType,
  helmReference: string,
  sequenceEntity: SequenceEntityGraphQL,
  forwardOffset: number | null
): Array<StructureGraphNode> {
  const xFn = d3
    .scaleLinear()
    .clamp(true)
    .domain([0, sequenceEntity.bases.length])
    .range([0, getWidthForBaseCount(sequenceEntity.bases.length)]);

  let leftOffset = 0;
  let yPosition = 0;
  if (
    complexPolymerType === ComplexPolymerType.OLIGO_DUPLEX &&
    helmReference === OLIGO_DUPLEX_REVERSE_STRAND_REF
  ) {
    assert(forwardOffset != null, 'Expected forward offset to be provided for oligo duplex');
    leftOffset = -getWidthForBaseCount(forwardOffset);

    yPosition = REVERSE_STRAND_NUCLEOTIDE_NODE_Y_POSITION;
  }

  const isForward = !(
    complexPolymerType === ComplexPolymerType.OLIGO_DUPLEX &&
    helmReference === OLIGO_DUPLEX_REVERSE_STRAND_REF
  );

  // The reverse oligo is displayed 3' to 5' from left to right and is also stored in this same direction.
  // The queried sequence entity is returned in 5' to 3' so we need to adjust here.
  const reverseAdjustedOligo = {
    bases: isForward ? sequenceEntity.bases : sequenceEntity.bases.split('').reverse().join(''),
    modifiedNucleotides: isForward
      ? sequenceEntity.modifiedNucleotides
      : sequenceEntity.modifiedNucleotides.map((mod) => ({
          ...mod,
          position: sequenceEntity.bases.length - 1 - mod.position,
        })),
    nucleotideType: sequenceEntity.nucleotideType,
  };

  return reverseAdjustedOligo.bases.split('').map((base, idx) => {
    const mod = reverseAdjustedOligo.modifiedNucleotides.find((mod) => mod.position === idx) ?? null;
    return {
      id: `${helmReference}-${idx}`,
      type: NodeType.NUCLEOTIDE_NODE,
      position: {
        x: leftOffset + letterStartX(xFn, 0, idx),
        y: yPosition,
      },
      draggable: false,
      selectable: false,
      data: {
        label: base,
        entityId: sequenceEntity.id,
        polymerRef: helmReference,
        residueIndex: idx,
        base,
        isForward,
        modifiedNucleotide: mod,
        nucleotideType: sequenceEntity.nucleotideType,
        width: NUCLEOTIDE_NODE_WIDTH,
        height: NUCLEOTIDE_NODE_HEIGHT,
      },
    };
  });
}

export function generatePolymerStructureGraphNodes(
  complexPolymerType: ComplexPolymerType,
  simplePolymers: Array<SimplePolymerGraphQL>,
  forwardOffset: number | null
): Array<StructureGraphNode> {
  const nodes: Array<StructureGraphNode> = [];

  simplePolymers.forEach((simplePolymer) => {
    if (isPolymerBackboneStrand(complexPolymerType, simplePolymer.helmReference)) {
      assert(simplePolymer != null, 'Expected backbone polymer to exist');
      assert(
        simplePolymer.entity.__typename === 'SequenceNode',
        'Expected backbone polymer to have sequence entity'
      );
      nodes.push(
        ...generateBackbonePolymerStructureGraphNodes(
          complexPolymerType,
          simplePolymer.helmReference,
          simplePolymer.entity,
          forwardOffset
        )
      );
    } else {
      assert(!isInaccessible(simplePolymer.entity), 'Expected accessible component entity');
      nodes.push(generateConjugateStructureGraphNode(simplePolymer.helmReference, simplePolymer.entity.id));
    }
  });

  return nodes;
}

function findNodeFromComplexPolymerConnectionSide(
  complexPolymerType: ComplexPolymerType,
  nodes: Array<StructureGraphNode>,
  connectionSide: ConnectionSideGraphQL
): StructureGraphNode {
  let matchingNodes = nodes.filter((node) => node.data.polymerRef === connectionSide.polymerHelmReference);

  if (isPolymerBackboneStrand(complexPolymerType, connectionSide.polymerHelmReference)) {
    matchingNodes = matchingNodes.filter((node) => {
      const nodeData = node.data as NucleotideStructureGraphNodeData;
      return nodeData.residueIndex === connectionSide.residueIndex!;
    });
  }

  assert(matchingNodes.length === 1, 'Expected exactly one matching node for a connection side');
  return matchingNodes[0];
}

export function generatePolymerStructureGraphEdges(
  complexPolymerType: ComplexPolymerType,
  nodes: Array<StructureGraphNode>,
  resolvedAttachments: Array<ResolvedAttachmentGraphQL>
): Array<StructureGraphEdge> {
  const edges: Array<StructureGraphEdge> = [];

  resolvedAttachments.forEach((resolvedAttachment) => {
    const orderedConnections = resolvedAttachment.connectionsInvolved;
    const maybeBackbonePolymer = getBackbonePolymerFromResolvedAttachment(
      complexPolymerType,
      resolvedAttachment
    );

    orderedConnections.forEach((connection) => {
      const startNode = findNodeFromComplexPolymerConnectionSide(
        complexPolymerType,
        nodes,
        connection.source
      );
      const endNode = findNodeFromComplexPolymerConnectionSide(complexPolymerType, nodes, connection.target);

      const isForward = !(
        complexPolymerType === ComplexPolymerType.OLIGO_DUPLEX &&
        maybeBackbonePolymer?.helmReference === OLIGO_DUPLEX_REVERSE_STRAND_REF
      );

      const sourceNode = isForward ? startNode : endNode;
      const targetNode = isForward ? endNode : startNode;

      edges.push({
        id: `${sourceNode.id}${targetNode.id}`,
        type: EdgeType.SMOOTHSTEP,
        source: sourceNode.id,
        target: targetNode.id,
        sourceHandle: HandleType.TOP,
        targetHandle: HandleType.BOTTOM,
      });
    });
  });

  return edges;
}

const CONJUGATE_CHIP_MARGIN = 100;

interface ConjugateGroup {
  polymerRefs: Array<string>;
  backbonePolymerRef: string | null;
  residueIndex: number | null;
}

function getBackboneConnectionSideFromResolvedAttachment(
  complexPolymerType: ComplexPolymerType,
  resolvedAttachment: ResolvedAttachmentGraphQL
): ConnectionSideGraphQL | null {
  const backbonePolymerRef =
    getRefsFromResolvedAttachment(resolvedAttachment).find((ref) =>
      isPolymerBackboneStrand(complexPolymerType, ref)
    ) ?? null;

  if (backbonePolymerRef == null) {
    return null;
  }

  const backboneConnectionSide = resolvedAttachment.connectionsInvolved
    .flatMap((connection) => [connection.source, connection.target])
    .find((connectionSide) => connectionSide.polymerHelmReference === backbonePolymerRef);
  assert(
    backboneConnectionSide != null && backboneConnectionSide.residueIndex != null,
    'Expected backbone connection to exist and have residue index populated'
  );
  return backboneConnectionSide;
}

function getRefsFromResolvedAttachment(resolvedAttachment: ResolvedAttachmentGraphQL): Array<string> {
  return resolvedAttachment.polymers.map((polymer) => polymer.helmReference);
}

function anyMatchingPolymerRefs(
  complexPolymerType: ComplexPolymerType,
  refsA: Array<string>,
  refsB: Array<string>
): boolean {
  // Returns whether there are any common polymer refs between two lists (not including backbone polymers)
  const matches = refsA
    .filter((refA) => refsB.includes(refA))
    .filter((match) => !isPolymerBackboneStrand(complexPolymerType, match));
  return matches.length > 0;
}

export function getConjugateGroupsFromResolvedAttachments(
  complexPolymerType: ComplexPolymerType,
  resolvedAttachments: Array<ResolvedAttachmentGraphQL>
): Array<ConjugateGroup> {
  // A single conjugate complex may be composed of more than one resolved attachment (particularly in the case
  // of certain non-molecule attachments). In order to layout the complex polymer, these resolved attachments
  // should be merged into their respective conjugate complexes.
  // Resolved attachments are merged if they have any of the same (non-backbone) polymer refs.
  const conjugateGroups: Array<ConjugateGroup> = [];
  resolvedAttachments.forEach((resolvedAttachment) => {
    // Find any potential existing conjugate group that this resolved attachment should be a part of.
    // If a match does not exist, start a new conjugate group for this resolved attachment.
    const maybeMatchingConjugateGroup = conjugateGroups.find((potentialConjugateGroupMatch) =>
      anyMatchingPolymerRefs(
        complexPolymerType,
        getRefsFromResolvedAttachment(resolvedAttachment),
        potentialConjugateGroupMatch.polymerRefs
      )
    );

    if (maybeMatchingConjugateGroup != null) {
      const mergedRefs = Array.from(
        new Set(
          maybeMatchingConjugateGroup.polymerRefs.concat(getRefsFromResolvedAttachment(resolvedAttachment))
        )
      );
      maybeMatchingConjugateGroup.polymerRefs = mergedRefs;
    } else {
      const backboneConnectionSide = getBackboneConnectionSideFromResolvedAttachment(
        complexPolymerType,
        resolvedAttachment
      );
      conjugateGroups.push({
        backbonePolymerRef: backboneConnectionSide?.polymerHelmReference ?? null,
        residueIndex: backboneConnectionSide?.residueIndex ?? null,
        polymerRefs: getRefsFromResolvedAttachment(resolvedAttachment),
      });
    }
  });

  return conjugateGroups;
}

interface PolymerStructureSubgraph {
  nodes: Array<StructureGraphNode>;
  edges: Array<StructureGraphEdge>;
  originNode: StructureGraphNode;
}

function getSubgraphForConjugateGroup(
  complexPolymerType: ComplexPolymerType,
  nodes: Array<StructureGraphNode>,
  edges: Array<StructureGraphEdge>,
  conjugateGroup: ConjugateGroup
): PolymerStructureSubgraph {
  // Get the subset of nodes and edges associated with a conjugate complex that will be laid out as a subgraph.
  // This subgraph has an origin node (the nucleotide node in a complex connected to a backbone residue, or
  // a randomly selected node in a conjugate-only complex). The origin node must stay in its original position
  // after layouting.
  const subgraphNodes = nodes.filter((node) => {
    if (isPolymerBackboneStrand(complexPolymerType, node.data.polymerRef)) {
      const nodeData = node.data as NucleotideStructureGraphNodeData;
      return (
        conjugateGroup.backbonePolymerRef === nodeData.polymerRef &&
        conjugateGroup.residueIndex === nodeData.residueIndex
      );
    } else {
      return conjugateGroup.polymerRefs.includes(node.data.polymerRef);
    }
  });

  const originNode = subgraphNodes.find((node) =>
    isPolymerBackboneStrand(complexPolymerType, node.data.polymerRef)
  ) ?? {
    // Take origin node as any polymer in a conjugate-only complex
    ...subgraphNodes[0],
    position: {
      x: 0,
      y: CONJUGATE_CHIP_MARGIN,
    },
  };

  const subgraphNodeIds = new Set(subgraphNodes.map((node) => node.id));
  const subgraphEdges = edges.filter(
    (edge) => subgraphNodeIds.has(edge.source) && subgraphNodeIds.has(edge.target)
  );

  return {originNode, nodes: subgraphNodes, edges: subgraphEdges};
}

function layoutPolymerStructureSubgraph(
  structureSubgraph: PolymerStructureSubgraph
): PolymerStructureSubgraph {
  // Generate a dagre graph containing a single conjugate complex and use dagre's autolayouting functionality
  // to layout that subgraph. Because origin nodes must have a fixed position, each subgraph (once laid out)
  // will be repositioned relative to the original position of the origin node.
  const {nodes, edges, originNode} = structureSubgraph;

  const dagreGraph = new dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({rankdir: 'BT'});

  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, {width: node.data.width, height: node.data.height});
  });
  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  // Get the necessary translation required for the origin node to be moved back to its original position
  const originNodeId = originNode.id;
  const originXTranslation =
    dagreGraph.node(originNodeId).x - originNode.data.width / 2 - originNode.position.x;
  const originYTranslation =
    dagreGraph.node(originNodeId).y - originNode.data.height / 2 - originNode.position.y;

  const newNodes = nodes.map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    const newNode = {
      ...node,
      // Two translations are done here:
      // 1. Shift the dagre node position which has an anchor at the center of the node, to the ReactFlow anchor
      // position at the top left.
      // 2. Translate node by the origin node offset, so that the origin node is back in its original position.
      position: {
        x: nodeWithPosition.x - node.data.width / 2 - originXTranslation,
        y: nodeWithPosition.y - node.data.height / 2 - originYTranslation,
      },
    };

    return newNode;
  });

  return {nodes: newNodes, edges, originNode};
}

const UNCONNECTED_NODE_VERTICAL_MARGIN = 80;
const UNCONNECTED_NODE_HORIZONTAL_MARGIN = 150;

function getUpperBoundOfNodeSet(nodes: Array<StructureGraphNode>): number {
  if (nodes.length === 0) {
    return 0;
  }
  return Math.min(...nodes.map((node) => node.position.y));
}

export function layoutUnconnectedConjugateNodes(
  connectedNodes: Array<StructureGraphNode>,
  unconnectedNodes: Array<StructureGraphNode>
): Array<StructureGraphNode> {
  const nodesVerticalPosition = getUpperBoundOfNodeSet(connectedNodes) - UNCONNECTED_NODE_VERTICAL_MARGIN;

  return unconnectedNodes.map((node, idx) => ({
    ...node,
    position: {x: idx * UNCONNECTED_NODE_HORIZONTAL_MARGIN, y: nodesVerticalPosition},
  }));
}

export function layoutPolymerStructureGraph(
  complexPolymerType: ComplexPolymerType,
  nodes: Array<StructureGraphNode>,
  edges: Array<StructureGraphEdge>,
  resolvedAttachments: Array<ResolvedAttachmentGraphQL>
): PolymerStructureGraph {
  const conjugateGroups = getConjugateGroupsFromResolvedAttachments(complexPolymerType, resolvedAttachments);
  const polymerStructureSubgraphs = conjugateGroups.map((conjugateGroup) =>
    getSubgraphForConjugateGroup(complexPolymerType, nodes, edges, conjugateGroup)
  );

  const layoutedNodes: Array<StructureGraphNode> = [];

  polymerStructureSubgraphs.forEach((subgraph) => {
    const {nodes: subgraphLayoutedNodes} = layoutPolymerStructureSubgraph(subgraph);
    layoutedNodes.push(...subgraphLayoutedNodes);
  });

  // Add back in all nucleotide nodes that are not part of an already laid out subgraph
  const nucleotideNodes = nodes.filter(
    (node) =>
      isPolymerBackboneStrand(complexPolymerType, node.data.polymerRef) &&
      !layoutedNodes.some((updatedNode) => node.id === updatedNode.id)
  );
  const connectedNodes = [...layoutedNodes, ...nucleotideNodes];

  // Place any nodes not in a conjugate structure in a line roughly above the the connected structure
  let unconnectedConjugateNodes = nodes.filter(
    (node) =>
      !isPolymerBackboneStrand(complexPolymerType, node.data.polymerRef) &&
      !layoutedNodes.some((updatedNode) => node.id === updatedNode.id)
  );
  unconnectedConjugateNodes = layoutUnconnectedConjugateNodes(connectedNodes, unconnectedConjugateNodes);

  return {nodes: [...connectedNodes, ...unconnectedConjugateNodes], edges};
}
